import React, { Component, useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import AuthenticationService from './AuthenticationService.js'


// ── Changelog data ────────────────────────────────────────────────────────────
const CHANGELOG = [
    {
        version: 'v2.9.8',
        date: '1 Jul 2026',
        highlight: true,
        entries: [
            { type: 'fix',     text: 'Declaraciones Provisionales: Corregido error 417 en PDFs con historial poblado (Febrero en adelante) — el parser ahora soporta sub-tablas históricas densas sin confundir sus valores con los campos reales' },
            { type: 'fix',     text: 'Declaraciones Provisionales: Resuelto crash "Cannot write uploaded file to disk" en Windows — la carga de PDF ya no depende del directorio temporal de Tomcat (usa stream directo con nombre único por upload)' },
            { type: 'fix',     text: 'Backend: Parser de PDF ahora es robusto ante cualquier cantidad de meses históricos declarados — los títulos de sub-tablas históricas se registran como encabezados reconocidos y se ignoran correctamente' },
            { type: 'fix',     text: 'Backend: Agregado fallback en validación de "Ingresos Nominales del Mes" — si el campo específico no se mapea, usa Total Ingresos como respaldo en lugar de rechazar el PDF' },
            { type: 'improve', text: 'Backend: Errores internos en carga de declaraciones ahora se registran con log.error (stack trace visible) en lugar de silenciarse — diagnóstico futuro sin necesidad de DEBUG manual' },
            { type: 'new',     text: 'Declaraciones Provisionales: Nueva pantalla de carga con stepper animado de 4 pasos — Carga del PDF → Descifrando contenido → Cargando contenido → ¡Éxito!' },
            { type: 'improve', text: 'Declaraciones Provisionales: Resumen visual al finalizar con resultado por archivo (exitoso / error), contador global y acciones de continuación' },
        ],
    },
    {
        version: 'v2.9.7',
        date: '16 Jun 2026',
        highlight: true,
        entries: [
            { type: 'fix',     text: 'Reporte Todos: Corregido bug crítico — los dropdowns de Año Inicio/Fin mostraban un año pero el estado interno tenía otro valor, generando columnas de años sin datos' },
            { type: 'fix',     text: 'Reporte Todos: La tabla ya no genera columnas para años fuera del rango con datos reales (safety net en generación del array de años)' },
            { type: 'fix',     text: 'Reporte Todos: Al cargar resultados, Año Inicio y Año Fin se auto-ajustan al rango mínimo/máximo de los datos disponibles' },
            { type: 'fix',     text: 'Sociedades: Corregido comportamiento al eliminar — ahora muestra mensaje claro si el servidor rechaza por registros relacionados (error 409/500)' },
            { type: 'improve', text: 'Reporte Todos: Toggle "Ver meses / Ocultar meses" para colapsar las columnas mensuales — por defecto muestra solo totales anuales' },
            { type: 'new',     text: 'Reporte Todos: Tarjetas de resumen con Total Ingresos y Total Nómina global de todas las sociedades visibles' },
            { type: 'improve', text: 'Reporte Todos: Dropdowns de Año Inicio/Fin muestran únicamente los años con datos reales cargados' },
            { type: 'fix',     text: 'Reporte Todos: Sociedades con $0.00 en el periodo seleccionado ya no aparecen en la tabla ni en los contadores de estatus' },
        ],
    },
    {
        version: 'v2.9.6',
        date: '10 Abr 2026',
        highlight: true,
        entries: [
            { type: 'fix',     text: 'Proyectos: Corregido error al seleccionar "Ver Todos" (id -1 incorrecto)' },
            { type: 'new',     text: 'Proyectos: Multi-selección en el listado — marca varios proyectos y ve el reporte combinado con un clic' },
            { type: 'improve', text: 'Reporte Todos: Al entrar se configura el periodo y se presiona Buscar para iniciar la carga (sin llamadas automáticas innecesarias)' },
            { type: 'improve', text: 'Reporte Todos: Campo "Clientes" renombrado a "Proyectos" para consistencia con la terminología de la app' },
            { type: 'improve', text: 'Reporte Todos: Botón Buscar siempre habilitado — si no hay proyectos filtrados, recarga todos' },
            { type: 'fix',     text: 'Sociedades: Identificado bug en backend — columna OBSERVACIONES del Excel se guardaba en campo Situación en lugar de Observaciones (pendiente corrección en backend)' },
        ],
    },
    {
        version: 'v2.9.5',
        date: '8 Abr 2026',
        highlight: true,
        entries: [
            { type: 'new',      text: 'Sociedades: Importación masiva desde archivo Excel (.xlsx / .xls)' },
            { type: 'security', text: 'Backend: Credenciales (AWS, BD, JWT) migradas a variables de entorno por seguridad' },
            { type: 'security', text: 'Backend: Eliminado el almacenamiento de contraseñas en texto plano' },
            { type: 'improve',  text: 'Backend: Implementado el manejo global de excepciones (@ControllerAdvice) para estandarizar las respuestas de error' },
            { type: 'improve',  text: 'Backend: Añadida validación estricta de inputs en los DTOs usando anotaciones (@Valid, @NotNull)' },
            { type: 'improve',  text: 'Backend: Reemplazo de salidas por consola, por un sistema de logging formal (SLF4J/Logback)' },
            { type: 'remove',   text: 'Backend: Limpieza de código legacy sin uso en producción' }
        ],
    },
    {
        version: 'v2.7.0',
        date: '6 Abr 2026',
        highlight: true,
        entries: [
            { type: 'improve', text: 'Core: Actualización mayor del framework principal de React 16 a React 18+' },
            { type: 'improve', text: 'Testing: Actualización crítica del entorno de pruebas (@testing-library/react v13 y user-event v14)' },
            { type: 'improve', text: 'Optimización: Limpieza y eliminación de dependencias obsoletas (react-native-blob-util, react-vis, moment)' },
            { type: 'improve', text: 'Dependencias: Actualización de mantenimiento para librerías base (axios, react-pdf, react-datepicker)' },
        ],
    },

    {
        version: 'v2.6.0',
        date: '21 Mar 2026',
        highlight: true,
        entries: [
            { type: 'new',     text: 'Usuarios: Fecha y hora de último ingreso en el listado' },
            { type: 'new',     text: 'Usuarios: Búsqueda en tiempo real' },
            { type: 'new',     text: 'Usuarios: Clonar permisos de un usuario a uno nuevo' },
            { type: 'new',     text: 'Usuarios: Forzar cierre de sesión sin necesidad de blacklist' },
            { type: 'new',     text: 'Usuarios: Renombrar usuario conservando permisos y contraseña' },
            { type: 'new',     text: 'Usuarios: Borrado real permanente con limpieza de historial' },
            { type: 'improve', text: 'Usuarios: Permisos del grupo Otros con etiquetas descriptivas' },
            { type: 'improve', text: 'Usuarios: Nombre de usuario inicia con mayúscula, mínimo 5 caracteres' },
        ],
    },
    {
        version: 'v2.5.3',
        date: '20 Mar 2026',
        entries: [
            { type: 'improve', text: 'Gastos del Domicilio: Formulario integrado en reporte, sin navegar a página externa' },
        ],
    },
    {
        version: 'v2.5.0',
        date: '15 Mar 2026',
        entries: [
            { type: 'new',     text: 'Reporte de Domicilios: Personas Físicas, Fotografías, Colapsable' },
            { type: 'new',     text: 'Reporte de Domicilios: Gastos del Domicilio, listado por ejercicio' },
            { type: 'improve', text: 'Domicilios: Filtros son persistentes' },
        ],
    },
    {
        version: 'v2.4.0',
        date: '14 Mar 2026',
        entries: [
            { type: 'improve', text: 'Sociedades: Filtros son persistentes' },
            { type: 'new',     text: 'Sociedades: Búsqueda y filtros por Domicilios' },
            { type: 'new',     text: 'Sociedades: Estatus Contable agregado al listado' },
        ],
    },
]

const ENTRY_META = {
    new:      { label: 'NUEVO',     color: '#34d399', bg: 'rgba(52,211,153,0.12)',  border: 'rgba(52,211,153,0.3)'  },
    improve:  { label: 'MEJORA',    color: '#818cf8', bg: 'rgba(129,140,248,0.12)', border: 'rgba(129,140,248,0.3)' },
    fix:      { label: 'ARREGLO',   color: '#fb923c', bg: 'rgba(251,146,60,0.12)',  border: 'rgba(251,146,60,0.3)'  },
    security: { label: 'SECURITY', color: '#f43f5e', bg: 'rgba(244,63,94,0.12)',   border: 'rgba(244,63,94,0.3)'   },
    remove:   { label: 'ELIMINADO',  color: '#9ca3af', bg: 'rgba(156,163,175,0.12)', border: 'rgba(156,163,175,0.3)' }
}
// ── Changelog Modal ───────────────────────────────────────────────────────────
function ChangelogModal({ onClose }) {
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        requestAnimationFrame(() => setVisible(true))
        const handler = (e) => { if (e.key === 'Escape') handleClose() }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [])

    const handleClose = () => {
        setVisible(false)
        setTimeout(onClose, 280)
    }

    return (
        <>
            <style>{`
                .cl-overlay {
                    position: fixed; inset: 0; z-index: 1000;
                    background: rgba(2, 6, 23, 0.65);
                    backdrop-filter: blur(8px);
                    -webkit-backdrop-filter: blur(8px);
                    display: flex; align-items: center; justify-content: center;
                    padding: 24px;
                    transition: opacity 0.28s ease;
                }
                .cl-overlay.cl-hidden { opacity: 0; pointer-events: none; }
                .cl-overlay.cl-shown  { opacity: 1; }

                .cl-modal {
                    position: relative;
                    width: 100%; max-width: 560px;
                    max-height: 82vh;
                    border-radius: 24px;
                    background: linear-gradient(145deg, rgba(15,23,42,0.97) 0%, rgba(15,20,40,0.97) 100%);
                    border: 1px solid rgba(255,255,255,0.1);
                    box-shadow:
                        0 0 0 1px rgba(255,255,255,0.04) inset,
                        0 32px 80px rgba(0,0,0,0.6),
                        0 0 100px rgba(99,102,241,0.08);
                    display: flex; flex-direction: column;
                    overflow: hidden;
                    transition: opacity 0.28s ease, transform 0.28s cubic-bezier(0.34,1.56,0.64,1);
                }
                .cl-modal.cl-hidden { opacity: 0; transform: translateY(24px) scale(0.96); }
                .cl-modal.cl-shown  { opacity: 1; transform: translateY(0) scale(1); }

                .cl-glow {
                    position: absolute; top: -80px; right: -60px;
                    width: 280px; height: 280px; border-radius: 50%;
                    background: radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 70%);
                    pointer-events: none;
                }

                .cl-header {
                    padding: 28px 28px 20px;
                    border-bottom: 1px solid rgba(255,255,255,0.07);
                    flex-shrink: 0;
                }
                .cl-header-top {
                    display: flex; align-items: flex-start;
                    justify-content: space-between; margin-bottom: 12px;
                }
                .cl-title {
                    font-size: 1.2rem; font-weight: 700;
                    color: #f8fafc; margin: 0 0 4px; letter-spacing: -0.3px;
                }
                .cl-subtitle { font-size: 0.75rem; color: rgba(255,255,255,0.35); margin: 0; }

                .cl-dismiss-btn {
                    display: flex; align-items: center; gap: 8px;
                    background: rgba(255,255,255,0.06);
                    border: 1px solid rgba(255,255,255,0.1);
                    color: rgba(255,255,255,0.55);
                    border-radius: 12px; padding: 8px 16px;
                    font-size: 0.8rem; font-weight: 600;
                    cursor: pointer; transition: all 0.18s; font-family: inherit; flex-shrink: 0;
                }
                .cl-dismiss-btn:hover {
                    background: rgba(255,255,255,0.1);
                    border-color: rgba(255,255,255,0.2);
                    color: #f1f5f9; transform: translateY(-1px);
                }
                .cl-dismiss-btn:active { transform: translateY(0); }

                .cl-latest-badge {
                    display: inline-flex; align-items: center; gap: 6px;
                    background: rgba(52,211,153,0.1); border: 1px solid rgba(52,211,153,0.25);
                    border-radius: 20px; padding: 4px 12px;
                    font-size: 0.7rem; font-weight: 700; color: #34d399; letter-spacing: 0.5px;
                }
                .cl-badge-dot {
                    width: 6px; height: 6px; border-radius: 50%;
                    background: #34d399; box-shadow: 0 0 8px #34d399;
                    animation: cl-pulse 2s infinite;
                }
                @keyframes cl-pulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50%       { opacity: 0.5; transform: scale(0.75); }
                }

                .cl-body {
                    overflow-y: auto; padding: 24px 28px 28px; flex: 1;
                    scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.1) transparent;
                }
                .cl-body::-webkit-scrollbar { width: 4px; }
                .cl-body::-webkit-scrollbar-track { background: transparent; }
                .cl-body::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 4px; }

                .cl-version-block { margin-bottom: 28px; }
                .cl-version-block:last-child { margin-bottom: 0; }
                .cl-version-row {
                    display: flex; align-items: center; gap: 10px; margin-bottom: 12px;
                }
                .cl-version-tag { font-size: 0.82rem; font-weight: 800; color: #e2e8f0; letter-spacing: 0.3px; }
                .cl-version-tag.cl-latest { color: #a5f3fc; }
                .cl-version-line { flex: 1; height: 1px; background: rgba(255,255,255,0.06); }
                .cl-version-date { font-size: 0.65rem; color: rgba(255,255,255,0.25); letter-spacing: 0.5px; }

                .cl-entry {
                    display: flex; align-items: flex-start; gap: 10px;
                    padding: 8px 0; border-bottom: 1px solid rgba(255,255,255,0.04);
                    animation: cl-fadeUp 0.3s ease both;
                }
                .cl-entry:last-child { border-bottom: none; }
                @keyframes cl-fadeUp {
                    from { opacity: 0; transform: translateY(8px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .cl-entry-pill {
                    font-size: 0.58rem; font-weight: 800; letter-spacing: 1px;
                    border-radius: 5px; padding: 3px 7px; flex-shrink: 0; margin-top: 1px;
                }
                .cl-entry-text { font-size: 0.8rem; color: rgba(255,255,255,0.6); line-height: 1.5; }

                .cl-footer {
                    padding: 14px 28px; border-top: 1px solid rgba(255,255,255,0.06);
                    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
                }
                .cl-footer-text { font-size: 0.68rem; color: rgba(255,255,255,0.2); }
            `}</style>

            <div
                className={`cl-overlay ${visible ? 'cl-shown' : 'cl-hidden'}`}
                onClick={(e) => { if (e.target === e.currentTarget) handleClose() }}
            >
                <div className={`cl-modal ${visible ? 'cl-shown' : 'cl-hidden'}`}>
                    <div className="cl-glow" />

                    <div className="cl-header">
                        <div className="cl-header-top">
                            <div>
                                <h2 className="cl-title">Cambios y mejoras</h2>
                                <p className="cl-subtitle">Cloud Zenyth · Historial de versiones</p>
                            </div>
                            <button className="cl-dismiss-btn" onClick={handleClose}>
                                <span>✕</span>
                                <span>Cerrar</span>
                            </button>
                        </div>
                        <div className="cl-latest-badge">
                            <div className="cl-badge-dot" />
                            Versión actual: {CHANGELOG[0].version} · {CHANGELOG[0].date}
                        </div>
                    </div>

                    <div className="cl-body">
                        {CHANGELOG.map((ver, vi) => (
                            <div className="cl-version-block" key={ver.version}>
                                <div className="cl-version-row">
                                    <span className={`cl-version-tag ${ver.highlight ? 'cl-latest' : ''}`}>
                                        {ver.version}
                                    </span>
                                    <div className="cl-version-line" />
                                    <span className="cl-version-date">{ver.date}</span>
                                </div>
                                {ver.entries.map((entry, ei) => {
                                    const meta = ENTRY_META[entry.type]
                                    return (
                                        <div
                                            className="cl-entry"
                                            key={ei}
                                            style={{ animationDelay: `${(vi * 3 + ei) * 0.04}s` }}
                                        >
                                            <span
                                                className="cl-entry-pill"
                                                style={{
                                                    background: meta.bg,
                                                    color: meta.color,
                                                    border: `1px solid ${meta.border}`,
                                                }}
                                            >
                                                {meta.label}
                                            </span>
                                            <span className="cl-entry-text">{entry.text}</span>
                                        </div>
                                    )
                                })}
                            </div>
                        ))}
                    </div>

                    <div className="cl-footer">
                        <span className="cl-footer-text">Cloud Zenyth by KrvheD · © 2026</span>
                    </div>
                </div>
            </div>
        </>
    )
}

// ── Main Component ────────────────────────────────────────────────────────────
class WelcomeComponent extends Component {

    constructor(props) {
        super(props)
        this.state = { hora: new Date(), showChangelog: false }
    }

    componentDidMount() {
        this.props.setUserLoggedIn(AuthenticationService.getLoggedInUserName())
        this.timer = setInterval(() => this.setState({ hora: new Date() }), 1000)
    }

    componentWillUnmount() {
        clearInterval(this.timer)
    }

    getGreeting() {
        const h = this.state.hora.getHours()
        if (h < 12) return 'Buenos días'
        if (h < 19) return 'Buenas tardes'
        return 'Buenas noches'
    }

    formatDate(date) {
        return date.toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
    }

    formatTime(date) {
        return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    }

    render() {
        const userName = AuthenticationService.getLoggedInUserName()
        const { hora, showChangelog } = this.state

        const modules = [
            { perm: "READ_ALERTAS",         to: "/alertas",          label: "Alertas",          icon: "🔔", desc: "Pendientes y avisos",        color: "#f59e0b" },
            { perm: "READ_SOCIEDADES",       to: "/sociedades",       label: "Sociedades",       icon: "🏢", desc: "Gestión de sociedades",       color: "#3b82f6" },
            { perm: "READ_CLIENTES",         to: "/clientes",         label: "Proyectos",        icon: "📁", desc: "Clientes y proyectos",        color: "#8b5cf6" },
            { perm: "READ_DOMICILIOS",       to: "/domicilios",       label: "Domicilios",       icon: "📍", desc: "Registro de domicilios",      color: "#10b981" },
            { perm: "READ_DOCUMENTOS",       to: "/documentos",       label: "Documentos",       icon: "📄", desc: "Gestión documental",          color: "#6366f1" },
            { perm: "READ_PERSONAS_FISICAS", to: "/personas-fisicas", label: "Personas Físicas", icon: "👤", desc: "Registro de personas",        color: "#ec4899" },
            { perm: "READ_CFDIS",            to: "/cfdis",            label: "CFDIs",            icon: "🧾", desc: "Facturas y comprobantes",     color: "#14b8a6" },
            { perm: "READ_HISTORIA",         to: "/historial",        label: "Historial",        icon: "📋", desc: "Registro de actividad",       color: "#f97316" },
            { perm: "READ_BUSQUEDA",         to: "/busqueda",         label: "Búsqueda",         icon: "🔍", desc: "Búsqueda general",            color: "#64748b" },
            { perm: "READ_USUARIOS",         to: "/users",            label: "Usuarios",         icon: "👥", desc: "Administración de usuarios",  color: "#a855f7" },
        ]

        const availableModules = modules.filter(m => AuthenticationService.haveAuthorization(m.perm))

        return (
            <div style={styles.root}>
                <style>{`
                    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
                    @keyframes fadeInUp {
                        from { opacity: 0; transform: translateY(16px); }
                        to   { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes fadeIn {
                        from { opacity: 0; }
                        to   { opacity: 1; }
                    }
                    .welcome-root * { font-family: 'Inter', sans-serif; box-sizing: border-box; }

                    .module-card {
                        background: white; border: 1px solid #e8edf5; border-radius: 16px;
                        padding: 24px 20px; text-decoration: none; display: flex;
                        flex-direction: column; gap: 10px; transition: all 0.2s;
                        animation: fadeInUp 0.5s ease forwards; opacity: 0;
                        cursor: pointer; position: relative; overflow: hidden;
                    }
                    .module-card::before {
                        content: ''; position: absolute; top: 0; left: 0; right: 0;
                        height: 3px; background: var(--card-color); border-radius: 16px 16px 0 0;
                    }
                    .module-card:hover {
                        transform: translateY(-4px);
                        box-shadow: 0 12px 32px rgba(0,0,0,0.1);
                        border-color: var(--card-color);
                    }
                    .module-icon {
                        width: 44px; height: 44px; border-radius: 12px;
                        display: flex; align-items: center; justify-content: center;
                        font-size: 1.3rem; background: var(--card-bg);
                    }
                    .module-label { font-size: 0.9rem; font-weight: 700; color: #1e293b; }
                    .module-desc  { font-size: 0.75rem; color: #94a3b8; font-weight: 400; }
                    .module-arrow {
                        font-size: 0.8rem; color: #cbd5e1; margin-top: auto;
                        transition: color 0.2s, transform 0.2s;
                    }
                    .module-card:hover .module-arrow { color: var(--card-color); transform: translateX(4px); }

                    .changelog-trigger {
                        display: inline-flex; align-items: center; gap: 6px;
                        font-size: 0.75rem; font-weight: 600; color: #475569;
                        background: rgba(52,211,153,0.06);
                        border: 1.5px solid rgba(52,211,153,0.5);
                        border-radius: 20px; padding: 4px 12px;
                        cursor: pointer; font-family: inherit;
                        animation: trigger-breathe 2.8s ease-in-out infinite;
                        transition: background 0.18s, border-color 0.18s, color 0.18s,
                                    transform 0.18s, box-shadow 0.18s;
                    }
                    .changelog-trigger:hover {
                        background: #0f172a; border-color: #0f172a;
                        color: #f1f5f9; transform: translateY(-1px);
                        box-shadow: 0 4px 12px rgba(15,23,42,0.15);
                        animation: none;
                    }
                    .changelog-trigger:active { transform: translateY(0); }
                    @keyframes trigger-breathe {
                        0%, 100% {
                            box-shadow: 0 0 0 0 rgba(52,211,153,0);
                            border-color: rgba(52,211,153,0.35);
                        }
                        50% {
                            box-shadow: 0 0 0 5px rgba(52,211,153,0.12),
                                        0 0 16px rgba(52,211,153,0.15);
                            border-color: rgba(52,211,153,0.8);
                        }
                    }
                    .changelog-trigger-dot {
                        width: 6px; height: 6px; border-radius: 50%;
                        background: #34d399; box-shadow: 0 0 6px #34d399;
                        animation: dot-pulse 2.5s infinite;
                    }
                    @keyframes dot-pulse {
                        0%, 100% { opacity: 1; } 50% { opacity: 0.4; }
                    }
                `}</style>

                <div className="welcome-root" style={styles.inner}>

                    <div style={styles.header}>
                        <div style={styles.headerLeft}>
                            <p style={styles.greeting}>{this.getGreeting()},</p>
                            <h1 style={styles.userName}>{userName} 👋</h1>
                            <p style={styles.date}>{this.formatDate(hora)}</p>
                        </div>
                        <div style={styles.clock}>
                            <span style={styles.clockTime}>{this.formatTime(hora)}</span>
                            <span style={styles.clockLabel}>Hora del sistema</span>
                        </div>
                    </div>

                    <div style={styles.divider} />

                    <div style={styles.sectionHeader}>
                        <div style={styles.sectionHeaderLeft}>
                            <h2 style={styles.sectionTitle}>Módulos disponibles</h2>
                            <button
                                className="changelog-trigger"
                                onClick={() => this.setState({ showChangelog: true })}
                            >
                                <span className="changelog-trigger-dot" />
                                Cambios y mejoras · {CHANGELOG[0].version}
                            </button>
                        </div>
                        <span style={styles.sectionCount}>{availableModules.length} accesos</span>
                    </div>

                    <div style={styles.grid}>
                        {availableModules.map((m, i) => (
                            <Link
                                key={m.to}
                                to={m.to}
                                className="module-card"
                                style={{
                                    '--card-color': m.color,
                                    '--card-bg': m.color + '18',
                                    animationDelay: `${i * 0.06}s`,
                                }}
                            >
                                <div className="module-icon">{m.icon}</div>
                                <div className="module-label">{m.label}</div>
                                <div className="module-desc">{m.desc}</div>
                                <div className="module-arrow">→</div>
                            </Link>
                        ))}
                    </div>

                    <div style={styles.footer}>
                        <span>Cloud Zenyth by KrvheD</span>
                        <span>·</span>
                        <span>© 2026</span>
                    </div>
                </div>

                {showChangelog && (
                    <ChangelogModal onClose={() => this.setState({ showChangelog: false })} />
                )}
            </div>
        )
    }
}

const styles = {
    root: {
        minHeight: '100vh',
        background: '#f1f5f9',
        padding: '32px 28px',
        fontFamily: "'Inter', sans-serif",
    },
    inner: { maxWidth: 1100, margin: '0 auto' },
    header: {
        display: 'flex', alignItems: 'flex-start',
        justifyContent: 'space-between', flexWrap: 'wrap', gap: 16,
        animation: 'fadeIn 0.6s ease forwards',
    },
    headerLeft: {},
    greeting:  { margin: '0 0 4px', fontSize: '0.9rem', color: '#64748b', fontWeight: 500 },
    userName:  { margin: '0 0 6px', fontSize: '2rem', fontWeight: 700, color: '#0f172a' },
    date:      { margin: 0, fontSize: '0.82rem', color: '#94a3b8', textTransform: 'capitalize' },
    clock: {
        background: 'white', border: '1px solid #e2e8f0', borderRadius: 14,
        padding: '14px 22px', textAlign: 'right', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    },
    clockTime: {
        display: 'block', fontSize: '1.5rem', fontWeight: 700,
        color: '#0f172a', letterSpacing: '1px', fontVariantNumeric: 'tabular-nums',
    },
    clockLabel: { fontSize: '0.68rem', color: '#94a3b8', letterSpacing: '1px', textTransform: 'uppercase' },
    divider:   { height: 1, background: '#e2e8f0', margin: '28px 0' },
    sectionHeader: {
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', marginBottom: 20, gap: 12, flexWrap: 'wrap',
    },
    sectionHeaderLeft: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
    sectionTitle: { margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1e293b' },
    sectionCount: {
        fontSize: '0.78rem', color: '#94a3b8', background: '#f1f5f9',
        border: '1px solid #e2e8f0', borderRadius: 20, padding: '3px 12px', fontWeight: 500,
    },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 },
    footer: {
        display: 'flex', gap: 8, justifyContent: 'center',
        marginTop: 40, fontSize: '0.72rem', color: '#cbd5e1',
    },
}

export default WelcomeComponent
